import api from "api/api";
import { Toast } from "components/toast";
import { useEffect, useState } from "react";
import {
  createWebSocketEventListener,
  makeWebSocketEvent,
} from "utils/web-socket-events";
import { AttendanceChatProps } from "../../../chats-context/types";
import { getToken } from "Session";
import { Attendance } from "components/atendimentos-components/atendimento-form/types";
import { useInitWebSocket } from "components/atendimentos-components/use-init-web-socket";
import { AttendanceActionsTypes } from "contexts/attendance-context/actions-reducer";
import { makeEvent } from "services/events";
import {
  ChatStatus,
  ContactInfo,
  Message,
  useWhatsAppSessionControlsProps,
  WhatsAppSession,
} from "./types";

export function useWhatsAppSessionControls({
  wsId,
  selectedAttendance,
  attendanceFullData,
  dispatch,
}: {
  wsId?: string;
  selectedAttendance?: AttendanceChatProps;
  attendanceFullData?: Attendance | Partial<Attendance>;
  dispatch: React.Dispatch<AttendanceActionsTypes>;
}): useWhatsAppSessionControlsProps {
  const [messages, setMessages] = useState<Map<string, Message>>(new Map());
  const [contactInfo, setContactInfo] = useState<Partial<ContactInfo>>({});
  const [chatStatus, setChatStatus] = useState<ChatStatus>("not-authenticated");
  const [whatsAppPhones, setWhatsAppPhones] = useState<WhatsAppSession[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [currentSession, setCurrentSession] = useState<WhatsAppSession>();
  const [connectHeaders, setConnectHeaders] = useState<any>();
  const url = process.env.REACT_APP_PUBLIC_WEB_SOCKET!;
  const wsSession = useInitWebSocket({
    headers: connectHeaders,
    isConnect: connectHeaders?.client != null,
    onMessage: ({ data }) => {
      const parsedData = JSON.parse(data);
      if (parsedData.contentType === "requested-media") {
        const listenerId = parsedData.requestedMedia.messageId;
        makeWebSocketEvent("requested-media", parsedData, listenerId);
      } else {
        makeWebSocketEvent(
          parsedData.state ?? parsedData.contentType,
          parsedData
        );
      }
    },
    url,
  });

  const attendanceId = selectedAttendance?.id || attendanceFullData?.id;
  const telefoneContatoNormalizado =
    selectedAttendance?.telefoneContatoNormalizado ||
    attendanceFullData?.telefoneContatoNormalizado;

  // WebSocket Events
  createWebSocketEventListener(
    "whatsapp-qrcode",
    (data) => setChatStatus("not-authenticated"),
    wsId
  );
  createWebSocketEventListener(
    "chat",
    (state) => setChatStatus("authenticated"),
    wsId
  );
  createWebSocketEventListener(
    "qrcode-generated",
    () => setChatStatus("not-authenticated"),
    wsId
  );
  createWebSocketEventListener(
    "qrcode-not-generated",
    (state) => setChatStatus("not-authenticated"),
    wsId
  );

  createWebSocketEventListener("contact-info", (data) => {
    if (attendanceId === data.attendanceId) {
      setContactInfo(data.contactInfo);
    }
  });

  createWebSocketEventListener("clear-whatsapp-messages", () => {
    setMessages(new Map());
  });

  // createWebSocketEventListener("whatsapp-messages", (data) => {
  // const messages = data.whatsappMessages;
  // setMessages(messages);
  // });

  createWebSocketEventListener("whatsapp-new-message", (data) => {
    const message = {
      ...data.whatsappNewMessage,
      attendanceId: data.attendanceId,
    };
    messages.set(message.id, message);
    setMessages(new Map(messages));
  });
  //

  const getPhones = async () => {
    setLoadingPhones(true);
    try {
      const { data } = await api.get("/whatsapp-sessions");
      setWhatsAppPhones(data);
    } catch (e: any) {
    } finally {
      setLoadingPhones(false);
    }
  };

  const changeWhatsAppSession = async (
    sessionId: string,
    session: WhatsAppSession
  ) => {
    if (attendanceId) {
      setLoadingPhones(true);
      try {
        await api.get(
          `/atendimentos/update-whatsapp-session/${attendanceId}/${sessionId}`
        );
        setCurrentSession(session);
        dispatch({
          type: "changeFieldWithInitial",
          payload: { fieldName: "whatsAppSession", data: session },
        });
      } catch (e: any) {
        Toast({
          title: "Não foi possível carregar os telefones whatsapp.",
          status: "error",
        });
      } finally {
        setLoadingPhones(false);
      }
    } else {
      throw new Error("É necessário um id de atendimento");
    }
  };

  const handleConnectWebSocket = async (attendanceFullData: Attendance) => {
    setConnectHeaders({
      token: getToken(),
      phone: attendanceFullData.telefoneContatoNormalizado,
      client: "tela-atendimento",
      whatsAppObjectId: attendanceFullData.whatsAppSession?.id,
      attendanceId: attendanceFullData.id,
    });
    setCurrentSession(attendanceFullData.whatsAppSession);
  };

  useEffect(() => {
    getPhones();
  }, []);

  useEffect(() => {
    setContactInfo({});
    setMessages(new Map());
  }, [attendanceId]);

  useEffect(() => {
    setConnectHeaders(undefined);
    if (attendanceFullData?.id) {
      handleConnectWebSocket(attendanceFullData as Attendance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attendanceFullData?.id,
    attendanceFullData?.telefoneContatoNormalizado,
    attendanceFullData?.whatsAppSession?.id,
  ]);

  useEffect(() => {
    setMessages(new Map());
  }, [telefoneContatoNormalizado]);

  return {
    contactInfo,
    chatStatus,
    whatsAppPhones,
    loadingPhones,
    changeWhatsAppSession,
    wsSession,
    currentSession,
    messages,
  };
}

export function clearWhatsAppMessages() {
  makeEvent("clear-whatsapp-messages", null, { throwsError: false });
}
